import { nextTick } from 'vue';
import { driver, type Config, type Driver } from 'driver.js';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { Disposable } from '@patsnap/tahiti-core';
import type { OnboardingBrowserRuntime } from '../onboarding-browser-runtime';

/** Onboarding 浏览器基础能力的通用实现，供各业务 runtime 继承。 */
@injectable()
export class OnboardingBrowserRuntimeImpl implements OnboardingBrowserRuntime {
    /** 从 localStorage 读取指定 key。 */
    getStorageItem(key: string): string | null {
        return window.localStorage.getItem(key);
    }

    /** 向 localStorage 写入指定 key。 */
    setStorageItem(key: string, value: string): void {
        window.localStorage.setItem(key, value);
    }

    /** 根据配置创建 Driver.js 实例。 */
    createDriver(config: Config): Driver {
        return driver(config);
    }

    /** 锁定嵌套页面容器的纵向滚动，并在释放时恢复样式与滚动位置。 */
    lockVerticalScroll(element: Element): Disposable {
        if (!(element instanceof HTMLElement)) {
            throw new TypeError('Onboarding scroll container must be an HTMLElement');
        }

        const originalOverflowY = element.style.overflowY;
        const originalOverscrollBehaviorY = element.style.overscrollBehaviorY;
        const originalScrollTop = element.scrollTop;
        let disposed = false;

        element.style.overflowY = 'hidden';
        element.style.overscrollBehaviorY = 'none';

        return {
            dispose: () => {
                if (disposed) return;
                disposed = true;
                element.style.overflowY = originalOverflowY;
                element.style.overscrollBehaviorY = originalOverscrollBehaviorY;
                element.scrollTop = originalScrollTop;
            },
        };
    }

    /** 等待 Vue 完成本轮 DOM 更新。 */
    waitForRender(): Promise<unknown> {
        return nextTick();
    }

    /** 等待异步目标出现，并在超时后释放 MutationObserver。 */
    waitForElement(selector: string, timeoutMs: number): Promise<Element | null> {
        const target = document.querySelector(selector);
        if (target) return Promise.resolve(target);

        return new Promise((resolve) => {
            const observer = new MutationObserver(() => {
                const element = document.querySelector(selector);
                if (!element) return;

                window.clearTimeout(timeoutId);
                observer.disconnect();
                resolve(element);
            });
            const timeoutId = window.setTimeout(() => {
                observer.disconnect();
                resolve(null);
            }, timeoutMs);

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
        });
    }

    /** 查询当前文档中的首个匹配元素。 */
    querySelector(selector: string): Element | null {
        return document.querySelector(selector);
    }

    /** 创建指定标签的 DOM 元素。 */
    createElement(tagName: string): HTMLElement {
        return document.createElement(tagName);
    }

    /** 创建浏览器延迟定时器。 */
    setTimeout(callback: () => void, timeoutMs: number): number {
        return window.setTimeout(callback, timeoutMs);
    }

    /** 清理浏览器延迟定时器。 */
    clearTimeout(timeoutId: number): void {
        window.clearTimeout(timeoutId);
    }

    /** 为指定事件目标注册监听。 */
    // eslint-disable-next-line max-params
    addEventListener(
        element: Element | Document,
        type: string,
        listener: EventListener,
        options?: boolean | AddEventListenerOptions,
    ): void {
        element.addEventListener(type, listener, options);
    }

    /** 从指定事件目标移除监听。 */
    // eslint-disable-next-line max-params
    removeEventListener(
        element: Element | Document,
        type: string,
        listener: EventListener,
        options?: boolean | EventListenerOptions,
    ): void {
        element.removeEventListener(type, listener, options);
    }

    /** 为 document 注册全局事件监听。 */
    addDocumentEventListener(type: string, listener: EventListener): void {
        document.addEventListener(type, listener);
    }

    /** 从 document 移除全局事件监听。 */
    removeDocumentEventListener(type: string, listener: EventListener): void {
        document.removeEventListener(type, listener);
    }

    /** 计算滚动容器 0 到 1 之间的阅读进度。 */
    getReadingProgress(element: Element): number {
        if (!(element instanceof HTMLElement)) return 0;

        const maxScrollTop = element.scrollHeight - element.clientHeight;
        if (maxScrollTop <= 0) return 0;
        return Math.min(1, Math.max(0, element.scrollTop / maxScrollTop));
    }

    /** 为目标元素添加样式类。 */
    addClass(element: Element, className: string): void {
        element.classList.add(className);
    }

    /** 从目标元素移除样式类。 */
    removeClass(element: Element, className: string): void {
        element.classList.remove(className);
    }

    /** 判断目标是否可交互，非按钮元素默认可用。 */
    isElementEnabled(element: Element): boolean {
        return !(element instanceof HTMLButtonElement) || !element.disabled;
    }

    /** 记录不会阻断页面主流程的异常。 */
    warn(message: string, error: unknown): void {
        console.warn(message, error);
    }
}
